/**
 * FinanceSummaryAI — F-30 · Resumen IA prudente del estado financiero.
 *
 * On-demand: no llama nunca al backend al cargar. Solo cuando el admin pulsa
 * "Generar resumen" / "Regenerar".
 *
 * Estados visuales:
 *   idle      → tarjeta neutra con botón "Generar resumen" + descripción.
 *   loading   → skeleton + "Analizando con IA…"
 *   ai        → texto + chip "IA" + timestamp + botón "Regenerar".
 *   fallback  → texto + chip "Sin IA" (gris, no alarmista) + botón "Regenerar".
 *   empty     → mensaje neutro "Aún no hay actividad financiera registrada."
 *               sin botón regenerar (no hay nada que regenerar).
 *   error     → mensaje breve, botón vuelve a estar activo.
 */
import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { Section } from "@/pages/Shell";
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";

const SOURCE_BADGE = {
  ai: { label: "IA", className: "bg-[#fbe8df] text-[#7a2c11] border-[#e8a685]" },
  fallback: { label: "Sin IA", className: "bg-stone-100 text-stone-600 border-stone-300" },
  empty: { label: null, className: "" },
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function FinanceSummaryAI() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const generate = async (refresh = false) => {
    setLoading(true); setErr("");
    try {
      const r = await api.post(`/finance/summary-ai${refresh ? "?refresh=true" : ""}`);
      setData(r.data);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Section
      title="Resumen ejecutivo"
      subtitle="Resumen prudente del estado financiero generado bajo demanda. Cacheado 6 horas."
    >
      <div className="dv-card" data-testid="finance-summary-ai">
        {/* Header always visible */}
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <Sparkle className="w-4 h-4 text-stone-400" />
            <span className="font-medium">Estado financiero</span>
            {data?.source && SOURCE_BADGE[data.source]?.label && (
              <span
                className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${SOURCE_BADGE[data.source].className}`}
                data-testid={`finance-summary-source-${data.source}`}
              >
                {SOURCE_BADGE[data.source].label}
              </span>
            )}
            {data?.generated_at && (
              <span className="text-xs text-stone-400">· {fmtTime(data.generated_at)}</span>
            )}
          </div>

          {/* Single action button on the right */}
          {!data && !loading && (
            <button
              onClick={() => generate(false)}
              className="dv-btn dv-btn-primary text-sm"
              data-testid="finance-summary-generate"
            >
              <Sparkle className="w-4 h-4" weight="fill" /> Generar resumen
            </button>
          )}
          {data && data.source !== "empty" && !loading && (
            <button
              onClick={() => generate(true)}
              className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900"
              data-testid="finance-summary-regenerate"
            >
              <ArrowClockwise className="w-3 h-3" /> Regenerar
            </button>
          )}
        </div>

        {/* Body */}
        {loading && (
          <div className="space-y-2 animate-pulse" data-testid="finance-summary-loading">
            <div className="h-3 bg-stone-100 rounded w-full" />
            <div className="h-3 bg-stone-100 rounded w-11/12" />
            <div className="h-3 bg-stone-100 rounded w-9/12" />
            <div className="text-xs text-stone-400 pt-2">Analizando con IA…</div>
          </div>
        )}

        {!loading && err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="finance-summary-error">
            {err}
          </div>
        )}

        {!loading && !data && !err && (
          <div className="text-sm text-stone-500" data-testid="finance-summary-idle">
            La IA producirá un resumen narrativo de 4 a 6 frases con las cifras y anomalías actuales del proyecto. Pulsa <strong>Generar resumen</strong> cuando lo necesites.
          </div>
        )}

        {!loading && data && (
          <>
            <p
              className="text-sm text-stone-800 leading-relaxed whitespace-pre-line"
              data-testid="finance-summary-text"
            >
              {data.summary}
            </p>
            {data.source === "fallback" && (
              <div className="text-xs text-stone-400 mt-3 italic">
                La IA no estuvo disponible. Resumen automático basado en los datos del proyecto.
              </div>
            )}
          </>
        )}
      </div>
    </Section>
  );
}
